import { useMemo } from 'react';
import { useVisitHistory } from './useVisitHistory';

type Notification = {
  date: Date;
  type: 'feature' | 'improvement' | 'bugfix' | 'notice';
  changes: string[];
};

type UseNotification = {
  notifications: readonly Notification[];
  hasNewNotifications: boolean;
};

const TODAY = new Date();
const THREE_MONTHS_AGO = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);

const NOTIFICATIONS = [
  {
    date: new Date(2026, 6, 31),
    type: 'feature',
    changes: [
      'カスタムエリアをGeoJSON形式でエクスポートできるようになりました。',
      'カスタムエリアの頂点数を変更できるようになりました。CmdまたはCtrlキーを押しながら辺や頂点をクリックすることで、頂点の追加や削除が可能です。',
      'カスタムエリアのインポート時に、MultiPolygonやFeatureCollectionに対応しました。',
    ],
  },
  {
    date: new Date(2026, 6, 10),
    type: 'feature',
    changes: [
      '地図の塗り分けの区分が自由に調整できるようになりました。',
      '同じ画角でスクリーンショットを撮れるよう、緯度経度を調整するための機能を用意しました。',
      'その他、パフォーマンスの改善やいくつかの不具合修正を行いました。',
    ],
  },
  {
    date: new Date(2026, 5, 19),
    type: 'improvement',
    changes: ['カスタムエリアのUIを改善し、名前検索できるようにしました。'],
  },
  {
    date: new Date(2026, 4, 29),
    type: 'improvement',
    changes: [
      '地図の塗りつぶしの色を変更できるようにしました。',
      'カスタムエリアの編集をより使いやすいように改善しました。',
    ],
  },
  {
    date: new Date(2026, 3, 17),
    type: 'feature',
    changes: [
      '通過地点の条件をAND/ORで切り替えできるようにしました。',
      'カスタムエリア（ポリゴン）をファイルからインポートできるようにしました。',
    ],
  },
  {
    date: new Date(2026, 3, 10),
    type: 'improvement',
    changes: [
      'Shiftを押しながらクリックすることで複数のエリアを選択できるようにしました。',
      '右側メニューのポリゴン操作UIを改善しました。',
    ],
  },
] as const satisfies readonly Notification[];

const useNotification = (): UseNotification => {
  const { lastVisited } = useVisitHistory();

  const notifications = useMemo((): readonly Notification[] => {
    return NOTIFICATIONS.filter((notification: Notification): boolean => {
      // 最終訪問時が3か月前よりも前の場合は、最終訪問時から今日までの通知を表示する
      if (lastVisited && lastVisited < THREE_MONTHS_AGO) {
        return lastVisited <= notification.date && notification.date <= TODAY;
      }
      // そうでない場合は、3か月前から今日までの通知を表示する
      return (
        THREE_MONTHS_AGO <= notification.date && notification.date <= TODAY
      );
    });
  }, [lastVisited]);

  const hasNewNotifications = useMemo((): boolean => {
    if (!lastVisited) {
      return true;
    }
    return NOTIFICATIONS.some((notification: Notification): boolean => {
      return notification.date > lastVisited;
    });
  }, [lastVisited]);

  return {
    notifications: notifications,
    hasNewNotifications: hasNewNotifications,
  };
};

export type { Notification };
export { useNotification };
